// ============================================================================
// THE GMAIL CARD'S WORDS — what the `?mail=` result means, in plain sentences.
//
// The Worker's OAuth callback sends the browser back to /settings/connections
// with `?mail=connected&account=<address>` or `?mail=error&reason=<code>`
// (apps/worker/src/routes/mail.ts → MAIL_CONNECT_ERRORS). The code is a fact;
// the sentence is this file's job, so a test can read every one of them.
// ============================================================================

export type MailResult =
  | { kind: 'connected'; account: string | null }
  | { kind: 'error'; reason: string; text: string }

/** One sentence per reason code. Each names what happened and, at most, one thing to do. */
export const MAIL_ERROR_TEXT: Record<string, string> = {
  not_configured: 'Google sign-in is not set up on this deployment yet, so nothing was connected.',
  no_database: 'The Worker has no database, so the account could not be saved.',
  denied: 'Google sign-in was cancelled. Nothing was connected.',
  google_error: 'Google stopped the sign-in. Nothing was connected — try again.',
  missing_code: 'Google came back without a sign-in. Try again.',
  bad_state: 'That sign-in link was not one LifeOS made. Start again from here.',
  expired_state: 'That sign-in took longer than ten minutes. Start again.',
  invalid_grant: 'That sign-in link was already used. Start again.',
  token_exchange_failed: 'Google refused the sign-in. The Worker’s Google client settings need checking.',
  google_unreachable: 'Google could not be reached. Try again in a minute.',
  scope_not_granted:
    'Gmail access was unticked on Google’s screen, so nothing was connected. Try again and leave it ticked.',
  no_refresh_token:
    'Google did not hand over a lasting sign-in. Remove LifeOS at myaccount.google.com/permissions, then connect again.',
  bad_id_token: 'Google did not say which account signed in. Try again.',
  no_identity: 'Google did not say which account signed in. Try again.',
  too_many_accounts: 'Three accounts are already connected. Disconnect one first.',
  server_error: 'Something went wrong saving the account. Try again.',
}

const UNKNOWN_ERROR = 'The Google sign-in did not finish, and it did not say why. Try again.'

/** Read the callback's result out of the query string. Null when there is none. */
export function readMailResult(params: URLSearchParams): MailResult | null {
  const mail = params.get('mail')
  if (mail === 'connected') return { kind: 'connected', account: params.get('account') || null }
  if (mail === 'error') {
    const reason = params.get('reason') ?? ''
    return { kind: 'error', reason, text: MAIL_ERROR_TEXT[reason] ?? UNKNOWN_ERROR }
  }
  return null
}

/** The line the card shows for a result. */
export function mailResultText(result: MailResult): string {
  if (result.kind === 'error') return result.text
  return result.account ? `Connected ${result.account}.` : 'Google account connected.'
}

/** The query string with the callback's three parameters taken out — read once, then cleared. */
export function withoutMailResult(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete('mail')
  next.delete('account')
  next.delete('reason')
  return next
}

/** The most Google accounts LifeOS reads. The Worker refuses a fourth (`too_many_accounts`). */
export const MAX_MAIL_ACCOUNTS = 3

/** An absolute URL is Google's consent screen; a path is demo mode staying inside the app. */
export function isInAppPath(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//')
}
